import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiFetch, ApiError } from '../lib/api'
import { humanizeEnum } from '../lib/labels'
import { HelpDot } from './guidance/Guidance'
import HouseDateInput from './HouseDateInput'
import { useAuthSession } from '../lib/authSession'
import { useRead } from './navigation/useRead'
import { recordQuery, type RecordContext } from './navigation/context'
import type { RecordTablePayload } from './recordTypes'
import { ErrorState } from './state'
import {
  MISSING_MOVEMENT,
  manualMovementSeed,
  manualMovementChoices,
  manualMovementBookLinks,
  type ManualMovementSeed,
} from './manualMovement'

/* Manual entry — the officer PROPOSES an object (E1, Ale ruling 2026-09-05).
 *
 * The 2026-09-04 UX audit (§15) found this form to be the one place in the
 * product where the doctrine has no gate: it wrote entity / instrument /
 * position / trade straight into the confirmed record, with no document and
 * no review, and the resulting object was indistinguishable from a
 * document-born one. It no longer writes anything. "Propose position" mints
 * a pending proposal that lands in the record-keeping queue as a book card
 * like any document-born one, carrying "officer entry · <name> · <date>"
 * wherever the object appears — and the record is unchanged until it books.
 *
 * Reused in TakeOverPane (the no-dead-ends escape hatch) and the standalone
 * /v2/manual page. Each proposed object is remembered so the next object in
 * the chain can reference it. */

type ObjType = 'entity' | 'instrument' | 'position' | 'trade'

const ENTITY_NATURES = ['legal_person', 'collective_vehicle', 'real_asset']
const INSTRUMENT_CATEGORIES = ['equity', 'debt', 'fictitious']
const POSITION_ROLES = ['single', 'commitment', 'undrawn']
const TRADE_TYPES = [
  'subscription',
  'acquisition',
  'redemption',
  'disposal',
  'capital_call',
  'distribution',
  'drawdown',
  'return_of_capital',
  'pre_existing_ownership',
  MISSING_MOVEMENT,
]

const ORDER: ObjType[] = ['entity', 'instrument', 'position', 'trade']
const LABELS: Record<ObjType, string> = {
  entity: 'Entity',
  instrument: 'Instrument',
  position: 'Position',
  trade: 'Trade',
}

interface Proposed {
  type: ObjType
  id: string
  label: string
  deckId: string
  movement?: { fundId: string; groupId: string }
}
interface FundOpt {
  id: string
  name: string
}

/** What POST /api/manual/* answers since the direct write was retired. */
interface ProposedObject {
  id: string
  fund_id: string
  target_type: string
  proposal_group_id: string
  deck_id: string
  status: string
  source_note: string
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

const inputStyle: React.CSSProperties = {
  font: 'inherit',
  fontSize: 'var(--text-12)',
  padding: '7px 10px',
  border: '1px solid var(--line)',
  borderRadius: 7,
  width: '100%',
  boxSizing: 'border-box',
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--text-12)',
  color: 'var(--muted)',
  marginBottom: 3,
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={fieldLabelStyle}>
        {label}
        {hint && <span style={{ color: 'var(--faint)' }}> · {hint}</span>}
      </span>
      {children}
    </label>
  )
}

/** A date field: the house control, with the label wired to its parts.
 *  Not inside <Field> — a <label> may name only one control, and this one
 *  has three (day / month / year), each with its own accessible name. */
function DateField({
  label,
  value,
  onChange,
  testId,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  testId?: string
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <span style={fieldLabelStyle}>{label}</span>
      <HouseDateInput label={label} value={value} onChange={onChange} testId={testId} />
    </div>
  )
}

function Sel({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder?: string
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
      <option value="">{placeholder ?? 'Choose…'}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {humanizeEnum(o)}
        </option>
      ))}
    </select>
  )
}

/** Picker over objects already proposed this session (+ free id entry). */
function RefPicker({
  value,
  onChange,
  proposed,
  ofType,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  proposed: Proposed[]
  ofType: ObjType
  placeholder: string
}) {
  const opts = proposed.filter((c) => c.type === ofType)
  return (
    <>
      <select
        value={opts.some((o) => o.id === value) ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      >
        <option value="">{placeholder}</option>
        {opts.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      {opts.length === 0 && (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste an existing id"
          className="mono"
          style={{ ...inputStyle, marginTop: 5, fontSize: 'var(--text-12)' }}
        />
      )}
    </>
  )
}

interface ManualEntryProps {
  fundId?: string
  onCreated?: (o: { id: string; target_type: ObjType }) => void
}

export default function ManualEntry(props: ManualEntryProps) {
  const [params] = useSearchParams()
  const session = useAuthSession()
  let movementSeed: ManualMovementSeed | null = null
  let contextError: string | null = null
  try {
    movementSeed = manualMovementSeed(params)
    if (movementSeed && props.fundId && props.fundId !== movementSeed.fundId)
      throw new Error('This movement link belongs to a different fund.')
  } catch (error) {
    contextError = errText(error)
  }
  return (
    <ManualEntryForm
      key={JSON.stringify([session, props.fundId, params.toString()])}
      {...props}
      movementSeed={movementSeed}
      contextError={contextError}
    />
  )
}

function ManualEntryForm({
  fundId,
  onCreated,
  movementSeed,
  contextError,
}: ManualEntryProps & {
  movementSeed: ManualMovementSeed | null
  contextError: string | null
}) {
  // Orphan → manual-entry deep link seeds the source document
  // (``/v2/manual?document_id=<id>``) so the doc is pre-filled (it surfaces on
  // the trade form's "Source document id" field).
  const [searchParams] = useSearchParams()
  const seededDocumentId = searchParams.get('document_id') ?? ''
  const embedded = !!fundId

  const [funds, setFunds] = useState<FundOpt[]>([])
  const [fundsLoaded, setFundsLoaded] = useState(false)
  const [fund, setFund] = useState<string>(fundId ?? movementSeed?.fundId ?? '')
  const [type, setType] = useState<ObjType>(movementSeed ? 'trade' : 'entity')
  const seedForm = (): Record<string, string> => ({
    ...(seededDocumentId ? { document_id: seededDocumentId } : {}),
    ...(movementSeed
      ? { transaction_type: MISSING_MOVEMENT, [`${movementSeed.kind}_id`]: movementSeed.id }
      : {}),
  })
  const [form, setForm] = useState<Record<string, string>>(seedForm)
  const [fundError, setFundError] = useState<unknown>(null)
  const [fundRetry, setFundRetry] = useState(0)
  const [recordRetry, setRecordRetry] = useState(0)
  const [bookRetry, setBookRetry] = useState(0)
  const [proposed, setProposed] = useState<Proposed[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [landed, setLanded] = useState<Proposed | null>(null)

  const missingMovement = type === 'trade' && form.transaction_type === MISSING_MOVEMENT
  const targetRead = useRead<RecordTablePayload>(
    fund && missingMovement ? `/api/funds/${encodeURIComponent(fund)}/record-table` : null,
    recordRetry
  )
  const targets = targetRead.value ? manualMovementChoices(targetRead.value, fund) : []
  const targetKind = form.position_id ? 'position' : 'instrument'
  const targetId = form.position_id || form.instrument_id
  const selectedTarget = targets.find((row) => row.kind === targetKind && row.id === targetId)
  const targetMismatch =
    !!movementSeed &&
    (fund !== movementSeed.fundId ||
      targetKind !== movementSeed.kind ||
      targetId !== movementSeed.id)
  const admittedMovement =
    missingMovement &&
    fundsLoaded &&
    funds.some((row) => row.id === fund) &&
    !!selectedTarget &&
    !targetMismatch &&
    !(form.position_id && form.instrument_id)
  const bookResult = landed?.movement ? { id: landed.id, ...landed.movement } : null
  const bookRead = useRead<RecordContext>(
    bookResult
      ? recordQuery({ kind: 'fund', fund_id: bookResult.fundId }, { proposed: true })
      : null,
    bookRetry
  )
  const bookLinks =
    bookResult && bookRead.value ? manualMovementBookLinks(bookRead.value, bookResult) : []

  useEffect(() => {
    const controller = new AbortController()
    setFundError(null)
    setFundsLoaded(false)
    apiFetch<FundOpt[]>('/api/funds', { signal: controller.signal })
      .then((d) => {
        if (controller.signal.aborted) return
        setFunds(d.map((f) => ({ id: f.id, name: f.name })))
        setFundsLoaded(true)
      })
      .catch((error) => {
        if (!controller.signal.aborted) setFundError(error)
      })
    return () => controller.abort()
  }, [fundRetry])

  const fundName = funds.find((f) => f.id === fund)?.name ?? null

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))
  const reset = () => {
    // Preserve the deep-linked source document across type switches so the
    // prefill survives until the officer reaches the trade form.
    setForm(seedForm())
    setError(null)
  }
  const switchType = (t: ObjType) => {
    setType(t)
    reset()
  }

  // payload per object type — only non-empty fields are sent
  const buildBody = useCallback((): Record<string, unknown> | string => {
    const v = (k: string) => (form[k] && form[k].trim() !== '' ? form[k].trim() : undefined)
    const base: Record<string, unknown> = { fund_id: fund }
    if (type === 'entity') {
      if (!v('canonical_name') || !v('entity_nature')) return 'Name and nature are required.'
      return {
        ...base,
        canonical_name: v('canonical_name'),
        entity_nature: v('entity_nature'),
        legal_form: v('legal_form'),
        jurisdiction: v('jurisdiction'),
        registered_address: v('registered_address'),
        incorporation_date: v('incorporation_date'),
      }
    }
    if (type === 'instrument') {
      if (!v('issuer_entity_id') || !v('category')) return 'Issuer and category are required.'
      return {
        ...base,
        issuer_entity_id: v('issuer_entity_id'),
        category: v('category'),
        commitment_based: form.commitment_based === 'true',
        name: v('name'),
        currency: v('currency'),
        outstanding_units: v('outstanding_units'),
        issuance_date: v('issuance_date'),
        maturity_date: v('maturity_date'),
      }
    }
    if (type === 'position') {
      if (!v('holder_entity_id') || !v('instrument_id'))
        return 'Holder and instrument are required.'
      // economic % is derived (units_held ÷ outstanding) — never entered here.
      return {
        ...base,
        holder_entity_id: v('holder_entity_id'),
        instrument_id: v('instrument_id'),
        units_held: v('units_held'),
        position_role: v('position_role') || 'single',
        effective_date: v('effective_date'),
      }
    }
    // trade
    if (!v('transaction_type')) return 'Transaction type is required.'
    if (Boolean(v('position_id')) === Boolean(v('instrument_id')))
      return 'Pick exactly one position or instrument the trade is against.'
    if (missingMovement) {
      if (!admittedMovement) return 'Choose an available confirmed target on this fund’s Record.'
      if (!v('effective_date')) return 'The actual movement date is required.'
      const units = v('units_delta') ?? ''
      if (
        !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(units) ||
        !/[1-9]/.test(units.split(/[eE]/)[0])
      )
        return 'Enter the known nonzero movement quantity.'
    }
    return {
      ...base,
      transaction_type: v('transaction_type'),
      position_id: v('position_id'),
      instrument_id: v('instrument_id'),
      document_id: v('document_id'),
      units_delta: v('units_delta'),
      amount: v('amount'),
      currency: v('currency'),
      effective_date: v('effective_date'),
    }
  }, [form, fund, type, missingMovement, admittedMovement])

  /** The sentence the book card will carry — built ONLY from what is typed,
   *  never padded with a placeholder (no "—", no invented figures). */
  const previewLine = (): string | null => {
    const v = (k: string) => (form[k] && form[k].trim() !== '' ? form[k].trim() : null)
    const nameOf = (id: string | null, of: ObjType) =>
      proposed.find((p) => p.type === of && p.id === id)?.label ?? null
    if (type === 'entity') {
      const n = v('canonical_name')
      return n ? `${n} enters the record as an entity` : null
    }
    if (type === 'instrument') {
      const n = v('name')
      const issuer = nameOf(v('issuer_entity_id'), 'entity')
      if (!n && !issuer) return null
      return [n ?? 'An instrument', issuer ? `issued by ${issuer}` : null]
        .filter(Boolean)
        .join(' — ')
    }
    if (type === 'position') {
      const holder = nameOf(v('holder_entity_id'), 'entity')
      const instrument = nameOf(v('instrument_id'), 'instrument')
      const units = v('units_held')
      if (!holder && !instrument && !units) return null
      const who = holder ?? 'The holder'
      const what = [units, instrument].filter(Boolean).join(' ')
      return what ? `${who} holds ${what}` : `${who} holds a position`
    }
    const kind = v('transaction_type')
    const units = v('units_delta')
    if (!kind) return null
    const label = kind === MISSING_MOVEMENT ? 'Officer-entered movement' : humanizeEnum(kind)
    return units
      ? `${label} of ${units} units${selectedTarget ? ' · ' + selectedTarget.label : ''}`
      : label
  }

  async function submit() {
    if (busy || contextError) return
    const body = buildBody()
    if (typeof body === 'string') {
      setError(body)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await apiFetch<ProposedObject>(
        `/api/manual/${type === 'entity' ? 'entities' : type + 's'}`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      )
      const label =
        form.canonical_name ||
        form.name ||
        (form.transaction_type === MISSING_MOVEMENT ? 'Missing movement' : form.transaction_type) ||
        `${LABELS[type]} ${res.id.slice(0, 6)}`
      const row: Proposed = {
        type,
        id: res.id,
        label,
        deckId: res.deck_id,
        ...(missingMovement ? { movement: { fundId: fund, groupId: res.proposal_group_id } } : {}),
      }
      setBookRetry(0)
      setProposed((c) => [row, ...c])
      setLanded(row)
      onCreated?.({ id: res.id, target_type: type })
      // advance to the next object in the chain
      const next = ORDER[Math.min(ORDER.indexOf(type) + 1, ORDER.length - 1)]
      switchType(next)
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  if (contextError)
    return (
      <div className="vcard" style={{ padding: 20 }}>
        <p role="alert">{contextError}</p>
        <Link to="/v2/funds">Return to Funds</Link>
      </div>
    )
  if (movementSeed && fundError)
    return (
      <ErrorState
        what="movement fund"
        error={fundError}
        onRetry={() => setFundRetry((value) => value + 1)}
      />
    )
  if (movementSeed && !fundsLoaded) return <p role="status">Reading the movement’s fund…</p>
  if (movementSeed && !funds.some((row) => row.id === fund))
    return (
      <div className="vcard" style={{ padding: 20 }}>
        <p role="alert">This movement’s fund is unavailable.</p>
        <Link to="/v2/funds">Return to Funds</Link>
      </div>
    )

  if (!fund) {
    return (
      <div className="vcard me-chooser" style={{ padding: 24, maxWidth: 480 }}>
        <div style={{ fontSize: 'var(--text-14)', fontWeight: 600, marginBottom: 6 }}>
          Manual entry — which fund?
        </div>
        <div style={{ fontSize: 'var(--text-13)', color: 'var(--muted)', marginBottom: 14 }}>
          Build the chain by hand: entity → instrument → position → trade. Each object is proposed
          into that fund’s record-keeping queue; the record does not change until you book it there.
        </div>
        {fundsLoaded && funds.length === 0 ? (
          <div style={{ fontSize: 'var(--text-13)', color: 'var(--muted)' }}>
            No funds on the record yet — manual entry attaches to a fund. The fund is created when
            its register entry syncs (see the Funds page); come back here once it exists.
          </div>
        ) : (
          <label style={{ display: 'block' }}>
            <span style={fieldLabelStyle}>Fund</span>
            <select
              aria-label="Fund"
              data-testid="manual-fund"
              value={fund}
              onChange={(e) => {
                setFund(e.target.value)
                setProposed([])
                setLanded(null)
                reset()
              }}
              style={inputStyle}
            >
              <option value="">Choose a fund…</option>
              {funds.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
    )
  }

  const sentence = previewLine()

  const formCard = (
    <div className="vcard" style={{ padding: 18, maxWidth: 560, flex: '1 1 420px' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {ORDER.map((t) => (
          <button
            key={t}
            onClick={() => switchType(t)}
            disabled={busy || (!!movementSeed && t !== 'trade')}
            className="vbtn"
            style={{
              flex: 1,
              fontSize: 'var(--text-12)',
              fontWeight: type === t ? 700 : 500,
              background: type === t ? 'var(--accent-ink)' : 'transparent',
              color: type === t ? '#fff' : 'var(--muted)',
            }}
          >
            {LABELS[t]}
          </button>
        ))}
        <HelpDot k="pane.manual_entry" />
      </div>

      {proposed.length > 0 && (
        <div style={{ marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {proposed.map((c) => (
            <span
              key={c.id}
              className="mono"
              title={`${c.id} — proposed, awaiting booking`}
              style={{
                fontSize: 'var(--text-12)',
                padding: '2px 8px',
                borderRadius: 10,
                border: '1px solid var(--line)',
                color: 'var(--muted)',
              }}
            >
              {LABELS[c.type].toLowerCase()}: {c.label.slice(0, 22)} · proposed
            </span>
          ))}
        </div>
      )}

      {/* ── per-type fields ── */}
      {type === 'entity' && (
        <>
          <Field label="Name" hint="required">
            <input
              style={inputStyle}
              value={form.canonical_name ?? ''}
              onChange={(e) => set('canonical_name', e.target.value)}
            />
          </Field>
          <Field label="Nature" hint="required">
            <Sel
              value={form.entity_nature ?? ''}
              onChange={(v) => set('entity_nature', v)}
              options={ENTITY_NATURES}
            />
          </Field>
          <Field label="Legal form">
            <input
              style={inputStyle}
              value={form.legal_form ?? ''}
              onChange={(e) => set('legal_form', e.target.value)}
            />
          </Field>
          <Field label="Jurisdiction">
            <input
              style={inputStyle}
              value={form.jurisdiction ?? ''}
              onChange={(e) => set('jurisdiction', e.target.value)}
            />
          </Field>
          <Field label="Registered address">
            <input
              style={inputStyle}
              value={form.registered_address ?? ''}
              onChange={(e) => set('registered_address', e.target.value)}
            />
          </Field>
          <DateField
            label="Incorporation date"
            value={form.incorporation_date ?? ''}
            onChange={(v) => set('incorporation_date', v)}
            testId="manual-incorporation-date"
          />
        </>
      )}

      {type === 'instrument' && (
        <>
          <Field label="Issuer entity" hint="required">
            <RefPicker
              value={form.issuer_entity_id ?? ''}
              onChange={(v) => set('issuer_entity_id', v)}
              proposed={proposed}
              ofType="entity"
              placeholder="Pick an entity…"
            />
          </Field>
          <Field label="Category" hint="required">
            <Sel
              value={form.category ?? ''}
              onChange={(v) => set('category', v)}
              options={INSTRUMENT_CATEGORIES}
            />
          </Field>
          <Field label="Commitment-based" hint="loans / LP / fund commitments">
            <input
              type="checkbox"
              checked={form.commitment_based === 'true'}
              onChange={(e) => set('commitment_based', e.target.checked ? 'true' : '')}
            />
          </Field>
          <Field label="Name">
            <input
              style={inputStyle}
              value={form.name ?? ''}
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>
          <Field label="Currency" hint="3-letter">
            <input
              style={inputStyle}
              maxLength={3}
              value={form.currency ?? ''}
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Units issued (outstanding)">
            <input
              style={inputStyle}
              value={form.outstanding_units ?? ''}
              onChange={(e) => set('outstanding_units', e.target.value)}
            />
          </Field>
          <DateField
            label="Issuance date"
            value={form.issuance_date ?? ''}
            onChange={(v) => set('issuance_date', v)}
            testId="manual-issuance-date"
          />
        </>
      )}

      {type === 'position' && (
        <>
          <Field label="Holder entity" hint="required">
            <RefPicker
              value={form.holder_entity_id ?? ''}
              onChange={(v) => set('holder_entity_id', v)}
              proposed={proposed}
              ofType="entity"
              placeholder="Pick the holder…"
            />
          </Field>
          <Field label="Instrument" hint="required">
            <RefPicker
              value={form.instrument_id ?? ''}
              onChange={(v) => set('instrument_id', v)}
              proposed={proposed}
              ofType="instrument"
              placeholder="Pick the instrument…"
            />
          </Field>
          <Field label="Units held" hint="ownership % is derived from units">
            <input
              style={inputStyle}
              value={form.units_held ?? ''}
              onChange={(e) => set('units_held', e.target.value)}
            />
          </Field>
          <Field label="Role">
            <Sel
              value={form.position_role ?? 'single'}
              onChange={(v) => set('position_role', v)}
              options={POSITION_ROLES}
              placeholder="single"
            />
          </Field>
          <DateField
            label="As of"
            value={form.effective_date ?? ''}
            onChange={(v) => set('effective_date', v)}
            testId="manual-effective-date"
          />
        </>
      )}

      {type === 'trade' && (
        <>
          {/* Was an <input list=…> + <datalist>, which browsers render as a
              plain text box with no chevron and no discoverable options — so
              the one REQUIRED field on the form was the only one an officer
              could not fill, while the two OPTIONAL fields below it had real
              pickers. Submit then failed with "Transaction type is required."
              (atlas 2026-07-29 p6 d7). The valid set is closed by a DB CHECK
              constraint, so a select is both usable and safer than free text. */}
          <Field label="Transaction type" hint="required">
            <select
              style={inputStyle}
              data-testid="manual-transaction-type"
              value={form.transaction_type ?? ''}
              onChange={(e) => set('transaction_type', e.target.value)}
              disabled={busy || !!movementSeed}
            >
              <option value="">Choose the transaction type…</option>
              {TRADE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t === MISSING_MOVEMENT
                    ? 'Missing movement (officer entry)'
                    : t.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </Field>
          {missingMovement ? (
            <Field label="Recorded target" hint="required">
              {fundError ? (
                <ErrorState
                  what="fund"
                  error={fundError}
                  onRetry={() => setFundRetry((value) => value + 1)}
                />
              ) : targetRead.error ? (
                <ErrorState
                  what="recorded target"
                  error={targetRead.error}
                  onRetry={() => setRecordRetry((value) => value + 1)}
                />
              ) : !fundsLoaded || targetRead.loading ? (
                <p role="status">Reading the current Record…</p>
              ) : movementSeed ? (
                <p data-testid="manual-movement-target">
                  {selectedTarget && !targetMismatch
                    ? selectedTarget.label
                    : 'The selected target is no longer available on this fund’s Record.'}
                </p>
              ) : (
                <select
                  aria-label="Recorded movement target"
                  style={inputStyle}
                  value={selectedTarget ? `${selectedTarget.kind}:${selectedTarget.id}` : ''}
                  onChange={(event) => {
                    const choice = targets.find(
                      (row) => `${row.kind}:${row.id}` === event.target.value
                    )
                    setForm((current) => ({
                      ...current,
                      position_id: choice?.kind === 'position' ? choice.id : '',
                      instrument_id: choice?.kind === 'instrument' ? choice.id : '',
                    }))
                  }}
                >
                  <option value="">Choose the recorded position or instrument…</option>
                  {targets.map((row) => (
                    <option key={`${row.kind}:${row.id}`} value={`${row.kind}:${row.id}`}>
                      {row.kind === 'position' ? 'Position' : 'Instrument'} · {row.label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : (
            <>
              <Field label="Against position">
                <RefPicker
                  value={form.position_id ?? ''}
                  onChange={(v) => set('position_id', v)}
                  proposed={proposed}
                  ofType="position"
                  placeholder="Pick a position…"
                />
              </Field>
              <Field label="…or instrument">
                <RefPicker
                  value={form.instrument_id ?? ''}
                  onChange={(v) => set('instrument_id', v)}
                  proposed={proposed}
                  ofType="instrument"
                  placeholder="Pick an instrument…"
                />
              </Field>
            </>
          )}
          <Field label="Units delta">
            <input
              style={inputStyle}
              value={form.units_delta ?? ''}
              onChange={(e) => set('units_delta', e.target.value)}
            />
          </Field>
          <Field label="Amount">
            <input
              style={inputStyle}
              value={form.amount ?? ''}
              onChange={(e) => set('amount', e.target.value)}
            />
          </Field>
          <Field label="Currency" hint="3-letter">
            <input
              style={inputStyle}
              maxLength={3}
              value={form.currency ?? ''}
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
            />
          </Field>
          <DateField
            label={missingMovement ? 'Movement value date (required)' : 'Effective date'}
            value={form.effective_date ?? ''}
            onChange={(v) => set('effective_date', v)}
            testId="manual-trade-effective-date"
          />
          <Field
            label="Source document id"
            hint={
              missingMovement ? 'optional — evidence remains open' : 'optional unless attestation'
            }
          >
            <input
              className="mono"
              style={{ ...inputStyle, fontSize: 'var(--text-12)' }}
              value={form.document_id ?? ''}
              onChange={(e) => set('document_id', e.target.value)}
            />
          </Field>
        </>
      )}

      {error && (
        <div style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', marginBottom: 8 }}>
          {error}
        </div>
      )}
      {landed && (
        <div
          style={{ fontSize: 'var(--text-12)', marginBottom: 8 }}
          data-testid="manual-proposed-flash"
        >
          <span style={{ color: 'var(--verified)' }}>✓</span> {LABELS[landed.type]} proposed — it is
          on the record-keeping queue, and the record is unchanged until it books.{' '}
          {landed.movement ? (
            <div>
              {bookLinks.map((link) => (
                <p key={link.href}>
                  <Link to={link.href}>Open Book · {link.label} ›</Link>
                </p>
              ))}
              {bookRead.loading ? <p role="status">Reading the new Book card…</p> : null}
              {bookRead.error ? (
                <ErrorState
                  what="new Book card"
                  error={bookRead.error}
                  onRetry={() => setBookRetry((value) => value + 1)}
                />
              ) : null}
              {!bookRead.loading && !bookRead.error && !bookLinks.length ? (
                <p>
                  The proposal is pending; its Book card is not yet available.{' '}
                  <button
                    type="button"
                    className="vbtn"
                    onClick={() => setBookRetry((value) => value + 1)}
                  >
                    Refresh Book card
                  </button>
                </p>
              ) : null}
            </div>
          ) : (
            <Link to={`/v2/proposals?fund=${encodeURIComponent(fund)}`}>Open the queue ›</Link>
          )}
        </div>
      )}

      <button
        className="vbtn pri"
        disabled={busy || (missingMovement && !admittedMovement)}
        onClick={submit}
        data-testid="manual-propose"
        style={{ marginTop: 4 }}
      >
        {busy ? 'Proposing…' : `Propose ${LABELS[type].toLowerCase()}`}
      </button>
      <div
        style={{
          fontSize: 'var(--text-12)',
          color: 'var(--muted)',
          marginTop: 10,
          lineHeight: 1.5,
        }}
      >
        Creates a NEW proposal in Record keeping{fundName ? ` — ${fundName}` : ''} · 1 segment. It
        books only when you approve it there; until then the record is unchanged.
      </div>
    </div>
  )

  const preview = (
    <div className="vcard" style={{ padding: 0, flex: '1 1 340px', maxWidth: 460 }}>
      <div
        style={{
          padding: '11px 16px',
          borderBottom: '1px solid var(--line)',
          fontSize: 'var(--text-12)',
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          fontWeight: 600,
        }}
      >
        What will be proposed
      </div>
      <div style={{ padding: '12px 16px', fontSize: 'var(--text-13)', lineHeight: 1.5 }}>
        {sentence ?? (
          <span style={{ color: 'var(--muted)' }}>
            Fill the form and the proposed {LABELS[type].toLowerCase()} is described here.
          </span>
        )}
      </div>
      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: '1px solid var(--line)',
          fontSize: 'var(--text-12)',
          color: 'var(--muted)',
          lineHeight: 1.5,
        }}
      >
        Provenance shown everywhere the object appears: officer entry · your name · today’s date.
      </div>
    </div>
  )

  if (embedded) return formCard

  return (
    /* The v2 shell's `.detail` carries no padding of its own — the page
     * supplies it, and scrolls inside the fixed-height column. */
    <div style={{ padding: '18px 26px 26px', overflow: 'auto', minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 14,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <h1 style={{ fontSize: 'var(--text-20)', margin: 0 }}>Manual entry</h1>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>Fund</span>
          <select
            aria-label="Fund"
            data-testid="manual-fund"
            disabled={busy || !!movementSeed}
            value={fund}
            onChange={(e) => {
              setFund(e.target.value)
              setProposed([])
              setLanded(null)
              reset()
            }}
            style={{ ...inputStyle, width: 'auto', minWidth: 200 }}
          >
            {funds.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 'var(--text-12)',
            color: 'var(--muted)',
            flex: '0 1 auto',
            minWidth: 0,
          }}
        >
          proposes into the <b style={{ color: 'var(--ink)' }}>record-keeping queue</b> · booked
          under your name · nothing reaches the record until it is booked
        </span>
      </div>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {formCard}
        {preview}
      </div>
    </div>
  )
}
